<script setup>
// The part of the Voice page that can be edited: the instruction blocks (the product sheet and the process go to
// text and voice; connections and voice notes go to voice only) and who answers the call. One field per block, with
// its limit; the page owns the form and saves it.
import { computed, useId } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  Field,
  FieldDescription,
  FieldError,
  FieldLabel,
} from 'dashboard/components-next/polaris/ui/field';
import { Textarea } from 'dashboard/components-next/polaris/ui/textarea';
import SettingRow from 'dashboard/components-next/polaris/cards/SettingRow.vue';

const props = defineProps({
  limits: { type: Object, required: true }, // { prompt_block, voice_prompt_extra }
  brains: { type: Array, required: true },
});
const form = defineModel({ type: Object, required: true });

const { t } = useI18n();
const uid = useId();

const blocks = computed(() =>
  [
    {
      id: 'productSheet',
      key: 'PRODUCT_SHEET',
      max: props.limits.prompt_block,
    },
    { id: 'process', key: 'PROCESS', max: props.limits.prompt_block },
    { id: 'connections', key: 'CONNECTIONS', max: props.limits.prompt_block },
    {
      id: 'voiceNotes',
      key: 'VOICE_NOTES',
      max: props.limits.voice_prompt_extra,
    },
  ].map(block => {
    const length = form.value[block.id].length;
    return {
      ...block,
      fieldId: `${uid}-${block.id}`,
      label: t(`POLARIS.ASSISTANTS.VOICE.FIELDS.${block.key}.LABEL`),
      help: t(`POLARIS.ASSISTANTS.VOICE.FIELDS.${block.key}.HELP`),
      counter: t('POLARIS.ASSISTANTS.VOICE.FIELDS.COUNTER', {
        count: length,
        max: block.max,
      }),
      tooLong: length > block.max,
    };
  })
);

const brainRow = computed(() => ({
  id: 'brain',
  kind: 'radio',
  label: t('POLARIS.ASSISTANTS.VOICE.FIELDS.BRAIN.LABEL'),
  help: t('POLARIS.ASSISTANTS.VOICE.FIELDS.BRAIN.HELP'),
  options: props.brains.map(brain => ({
    value: brain,
    label: t(
      `POLARIS.ASSISTANTS.VOICE.FIELDS.BRAIN.OPTIONS.${brain.toUpperCase()}`
    ),
  })),
}));

const set = (field, value) => {
  form.value = { ...form.value, [field]: value };
};
</script>

<template>
  <Field
    v-for="block in blocks"
    :key="block.id"
    :data-invalid="block.tooLong || undefined"
    :data-voice-block="block.id"
  >
    <FieldLabel :for="block.fieldId">{{ block.label }}</FieldLabel>
    <Textarea
      :id="block.fieldId"
      :model-value="form[block.id]"
      class="max-h-80 overflow-y-auto"
      :aria-invalid="block.tooLong || undefined"
      :aria-describedby="`${block.fieldId}-help`"
      @update:model-value="value => set(block.id, value)"
    />
    <div class="flex flex-wrap items-start justify-between gap-x-4 gap-y-1">
      <FieldDescription
        :id="`${block.fieldId}-help`"
        class="type-polaris-caption"
      >
        {{ block.help }}
      </FieldDescription>
      <FieldError v-if="block.tooLong">{{ block.counter }}</FieldError>
      <span
        v-else
        class="shrink-0 type-polaris-caption text-polaris-muted-foreground"
      >
        {{ block.counter }}
      </span>
    </div>
  </Field>
  <SettingRow
    :model-value="form.brain"
    :row="brainRow"
    @update:model-value="value => set('brain', value)"
  />
</template>
